import React, { useRef } from 'react';
import { Box, Divider, TextField, Typography } from '@mui/material';
import {
  FormProps,
  FormHeader,
  ClientNameField,
  DocumentUploadArea,
  AudioRecordingSection,
  WritingStyleSelector,
  InfoTip,
} from './shared';

const ConsignacaoPagamentoTrabalhistaForm: React.FC<FormProps> = ({
  documentType,
  values,
  onChange,
  uploadedFiles,
  onFileUpload,
  onFileRemove,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedStyle, setSelectedStyle] = React.useState(values.estilo_escrita || 'padrao');

  const handleFieldChange = (fieldId: string, value: any) => {
    onChange({
      ...values,
      [fieldId]: value,
    });
  };

  const handleRecordingComplete = (audioBlob: Blob) => {
    const audioFile = new File([audioBlob], `audio_${Date.now()}.webm`, {
      type: 'audio/webm',
    });
    onFileUpload([audioFile]);
  };

  return (
    <Box>
      <FormHeader 
        documentType={documentType}
        customTitle="Ação de Consignação em Pagamento (Trabalhista)"
        customDescription="Ação para depósito judicial de verbas trabalhistas quando há recusa ou impossibilidade de recebimento"
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* 1. Nome do Cliente */}
        <ClientNameField
          value={values.nome_cliente || ''}
          onChange={(value) => handleFieldChange('nome_cliente', value)}
          label="Escreva o nome do seu cliente:"
        />

        {/* 2. Fatos do Caso (Obrigatório) */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Quais são os fatos do caso? <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={8}
            placeholder="Ex: A empresa consignante tentou efetuar o pagamento das verbas rescisórias ao ex-empregado, porém este se recusou a receber, alegando discordância dos valores. Diante da recusa injustificada, a empresa busca a consignação em pagamento para se exonerar da obrigação..."
            value={values.fatos_caso || ''}
            onChange={(e) => handleFieldChange('fatos_caso', e.target.value)}
            inputProps={{ maxLength: 10000 }}
          />
          <Typography 
            variant="caption" 
            color={(values.fatos_caso || '').length >= 200 ? 'success.main' : 'text.secondary'} 
            sx={{ mt: 0.5, display: 'block' }}
          >
            {(values.fatos_caso || '').length} de 10000 caracteres permitidos
            {(values.fatos_caso || '').length < 200 && ` (mínimo: 200)`}
            {(values.fatos_caso || '').length >= 200 && ' ✓'}
          </Typography>
        </Box>

        {/* 3. Parte Contrária */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Escreva o nome da parte contrária: <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            placeholder="Ex: O réu é Manuel Pereira."
            value={values.parte_contraria || ''}
            onChange={(e) => handleFieldChange('parte_contraria', e.target.value)}
            helperText="Nome do trabalhador ou ex-empregado"
          />
        </Box>

        {/* 4. Upload de Documentos */}
        <DocumentUploadArea
          uploadedFiles={uploadedFiles}
          onFileUpload={onFileUpload}
          onFileRemove={onFileRemove}
          suggestedDocs="TRCT, comprovantes de tentativa de pagamento, notificações e comunicações ao empregado."
          fileInputRef={fileInputRef as React.RefObject<HTMLInputElement>}
        />

        {/* Gravação de Áudio */}
        <AudioRecordingSection
          onRecordingComplete={handleRecordingComplete}
          description="Grave um áudio descrevendo os fatos do caso que a IA irá transcrever automaticamente."
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Estilo de Escrita */}
      <WritingStyleSelector
        selectedStyle={selectedStyle}
        onStyleChange={(styleId) => {
          setSelectedStyle(styleId);
          handleFieldChange('estilo_escrita', styleId);
        }}
      />

      <InfoTip message="Na ação de consignação em pagamento trabalhista, demonstre a tentativa de pagamento e a recusa ou impossibilidade de recebimento. Anexe comprovantes de notificação e os valores que se pretende consignar." />
    </Box>
  );
};

export default ConsignacaoPagamentoTrabalhistaForm;

